import { AsyncPipe } from '@angular/common';
import { Component, Input, inject, input, OnChanges, OnInit, SimpleChanges, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IBbitWebShopItemPriceResponse } from '@bbitgmbh/bbit.utils';
import {
  BbitWebShopApiService,
  BbitWebShopCartService,
  BbitWebShopFormatPricePipe,
  BbitWebShopService,
  BbitWebShopVatCalculationMethod,
} from '@bbitgmbh/webshop.sdk';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { BehaviorSubject, combineLatest, distinctUntilChanged, switchMap } from 'rxjs';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-item-price',
  templateUrl: './item-price.component.html',
  styleUrls: ['./item-price.component.scss'],
  imports: [LoadingComponent, AsyncPipe, BbitWebShopFormatPricePipe, I18nPipe],
})
export class ItemPriceComponent implements OnInit, OnChanges {
  private _api = inject(BbitWebShopApiService);
  website = inject(BbitWebsiteService);
  webshopService = inject(BbitWebShopService);
  cart = inject(BbitWebShopCartService);

  public canBeSold = signal(true);
  public isLoading = new BehaviorSubject(true);
  public currentId = new BehaviorSubject<string | undefined>(undefined);
  public priceSubject = new BehaviorSubject<IBbitWebShopItemPriceResponse | undefined>(undefined);
  public VatCalculationMethod = BbitWebShopVatCalculationMethod;

  readonly item = input<string>();
  readonly doNotShowLoadingSpinner = input(false);
  @Input()
  set price(price: IBbitWebShopItemPriceResponse | undefined | null) {
    if (price) {
      this.priceSubject.next(price);
    }
  }
  readonly showMSRP = input(true);

  constructor() {
    this.priceSubject.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isLoading.next(false);
    });

    combineLatest([this.currentId.asObservable().pipe(distinctUntilChanged())])
      .pipe(
        switchMap(async ([currentId]) => {
          if (currentId) {
            this.isLoading.next(true);
            try {
              const result = await this._api.getItemPriceAndStock(currentId);
              this.priceSubject.next(result.price);
              this.canBeSold.set(result.canBeSold);
            } catch (err) {
              console.error('Error while loading price', currentId, err);
              this.priceSubject.next(undefined);
              this.canBeSold.set(false);
            }
            this.isLoading.next(false);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  async ngOnInit() {
    this.currentId.next(this.item());
  }

  ngOnChanges(changes: SimpleChanges) {
    const priceChange = changes['price'];
    if (priceChange?.currentValue) {
      this.priceSubject.next(priceChange.currentValue);
      return;
    }
    const itemChange = changes['item'];
    if (itemChange?.currentValue !== itemChange?.previousValue) {
      this.currentId.next(itemChange.currentValue);
    }
  }
}
